import { createElement, Children, cloneElement, Fragment } from 'react';

const TAG_NAME = 'em';
export const SEARCH_RESULT_MATCH_TAG = TAG_NAME;

type EmphasizeState = {
  textIndex: number;
  matchCounter: number;
};

const highlight = (child: string, key: number, tagName: string) =>
  // Highlight-js uses raw ints as keys, so we can't use them without possibly clashing.
  // So instead we use `em-<int>` as the key.
  createElement(tagName, { key: `${tagName}-${key}` }, child);

// Exported for testing, do not call
export function processStringChild(
  stringElement: string,
  positions: Array<[number, number]>,
  state: EmphasizeState,
  tagName: string
): JSX.Element | string {
  const end = state.textIndex + stringElement.length;

  // find position matches that overlap
  const overlappingPos = positions
    .filter(position => {
      const matchStart = position[0];
      const matchEnd = position[1];
      return matchStart < end && matchEnd > state.textIndex;
    })
    // Convert all the position pairs to be relative to this string.
    // Position 0 could now be negative, and position 1 could be beyond the length of the string.
    .map(position => {
      return [position[0] - state.textIndex, position[1] - state.textIndex];
    });

  // Move the state along, now that we are done reading it, and we might exit on the next line
  state.textIndex += stringElement.length;

  if (overlappingPos.length === 0) {
    // There are no matches in this element so lets bail
    return stringElement;
  }

  const newElements: (JSX.Element | string)[] = [];
  let processedChars = 0;
  overlappingPos.forEach(position => {
    // This match starts before where we are
    if (position[0] <= 0) {
      const matchingChars = stringElement.substring(0, position[1]);
      processedChars += matchingChars.length;
      newElements.push(highlight(matchingChars, state.matchCounter++, tagName));
    } else {
      // Add what ever was in front of this match
      const nonMatchingChars = stringElement.substring(
        processedChars,
        position[0]
      );
      newElements.push(nonMatchingChars);
      processedChars += nonMatchingChars.length;

      const matchingChars = stringElement.substring(position[0], position[1]);
      processedChars += matchingChars.length;
      newElements.push(highlight(matchingChars, state.matchCounter++, tagName));
    }
  });

  // If needed add the rest of the string as an element
  if (processedChars < stringElement.length) {
    newElements.push(stringElement.substring(processedChars));
  }

  if (newElements.length === 1) {
    return newElements[0];
  }
  return <Fragment>{newElements}</Fragment>;
}

export function processReactChild(
  element: JSX.Element,
  positions: Array<[number, number]>,
  state: EmphasizeState,
  tagName: string
): JSX.Element | string {
  const newChildren = Children.map(element.props.children, child => {
    const processedChild = processAny(child, positions, state, tagName);
    return processedChild;
  });

  return cloneElement(element, {}, newChildren);
}

function processAny(
  element: JSX.Element | string,
  positions: Array<[number, number]>,
  state: EmphasizeState,
  tagName: string
): JSX.Element | string {
  if (typeof element === 'string') {
    return processStringChild(element, positions, state, tagName);
  }

  return processReactChild(element, positions, state, tagName);
}

export default function emphasizeMatches(
  element: JSX.Element | string,
  positions: Array<[number, number]>,
  tagName = SEARCH_RESULT_MATCH_TAG
): JSX.Element | string {
  const initialState = {
    textIndex: 0,
    matchCounter: 0,
  };

  return processAny(element, positions, initialState, tagName);
}
